/* eslint-disable */
import { CloseOutlined } from '@ant-design/icons';
import { Form, message, Spin } from 'antd';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import io from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { API, rootAPI } from '../../config/api';
import { basicAuth } from '../../config/auth';
import { useAppData } from '../../hooks/useApp';
import { useUser } from '../../hooks/useUser';
import { addMessage } from '../../redux/messages/actionCreator';
import ChatMessage from './ChatMessage';
import {
  CameraIcon,
  Heading,
  MicrophoneIcon,
  StyledInput,
  StyledNamearagraph,
  StyledStatusParagraph,
} from './styled';
import VoiceMessage from './VoiceMessage';
import RecordingIndicator from './RecordingIndicator';
import VoiceOverlay from './VoiceOverlay';
import WaitingAnimation from './WaitingAnimation';
import { useConversationScroll } from './useConversationScroll';

const Conversation = () => {
  const audioRef = useRef(null);
  const fileInputRef = useRef();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const messageContentRef = useRef(null);
  const followLatest = useConversationScroll(scrollContainerRef, messageContentRef);
  const [isChecked, setIsChecked] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('auto');
  const [senderId, setSenderId] = useState('');
  const [textMessage, setTextMessage] = useState('');
  const [socket, setSocket] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const uploadedFilesRef = useRef([]);
  uploadedFilesRef.current = uploadedFiles;
  const [isHumanAgentActive, setIsHumanAgentActive] = useState(false);
  const [displayAnimation, setDisplayAnimation] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [startRecording, setStartRecording] = useState('');
  const [inputType, setInputType] = useState('text');
  const { appId } = useParams();
  const [error, setError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [recordingStopSignal, setRecordingStopSignal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finalImageSrc, setFinalImageSrc] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const { stateMessages } = useSelector((state) => {
    return {
      stateMessages: state.messages.messages,
    };
  });

  const { app } = useAppData(appId);
  const { user, refetch } = useUser(app?.app_id);
  useEffect(() => {
    if (app?.image) {
      const serverImgUrl = `${rootAPI}/uploads/images/${app.image}`;
      const img = new Image();
      img.src = serverImgUrl;

      img.onload = () => {
        setFinalImageSrc(serverImgUrl);
        setIsImageLoading(false);
      };

      img.onerror = () => {
        setFinalImageSrc(require('../../static/img/banner/newPlaceholer.png'));
        setIsImageLoading(false);
      };
    } else if (app) {
      setFinalImageSrc(require('../../static/img/banner/newPlaceholer.png'));
      setIsImageLoading(false);
    }
  }, [app]);

  const toggleSwitch = () => {
    const newValue = !isChecked;
    setIsChecked(newValue);
    setCurrentLanguage(newValue ? 'bn-BD' : 'en-US');
  };

  // SOCKET INITIALIZATION START
  useEffect(() => {
    const newSocket = io(`${rootAPI}`);
    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, []);

  useEffect(() => {
    if (socket && senderId) {
      const joinRoom = () => {
        // console.log('📡 Attempting to join room:', senderId);
        socket.emit('join_conversation', senderId);
      };

      if (socket.connected) {
        joinRoom();
      }

      socket.on('connect', () => {
        // console.log('✅ Socket Reconnected');
        joinRoom();
      });

      const handleReceiveMessage = (incomingMsg) => {
        // console.log('📩 Message Received via Socket:', incomingMsg);

        if (incomingMsg.sender === 'ai' || incomingMsg.sender === 'AI' || incomingMsg.sender === 'assistant') {
          setDisplayAnimation(false);
          dispatch(addMessage(incomingMsg));
        }
      };

      const handleAgentStatus = (data) => {
        setIsHumanAgentActive(data.isHumanAgentActive);
      };

      socket.on('receive_message', handleReceiveMessage);
      socket.on('agent_status_update', handleAgentStatus);

      return () => {
        socket.off('connect');
        socket.off('receive_message');
        socket.off('agent_status_update');
      };
    }
  }, [socket, senderId]);
  // SOCKET INITIALIZATION END

  // HANDLE SESSION
  useEffect(() => {
    const sessionId = sessionStorage.getItem('senderId');
    if (sessionId) {
      setSenderId(sessionId);
    } else {
      const newId = uuidv4();
      sessionStorage.setItem('senderId', newId);
      setSenderId(newId);
    }
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (isSubmitting) { message.info('Please wait for the current message to finish.'); return; }
    const newFiles = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setUploadedFiles((previous) => [...previous, ...newFiles]);
  };

  // Remove file from preview
  const removeFile = (index) => {
    const newFiles = [...uploadedFiles];
    URL.revokeObjectURL(newFiles[index].preview);
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  // Cleanup object URLs
  useEffect(() => {
    return () => uploadedFilesRef.current.forEach((file) => URL.revokeObjectURL(file.preview));
  }, []);

  // Handle audio playback events
  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);
    audioElement.addEventListener('ended', handleEnded);

    return () => {
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
      audioElement.removeEventListener('ended', handleEnded);
    };
  }, []);

  // HANDLE HTTP REQUEST START
  const handleConversation = async (voice = null) => {
    const voiceInput = voice?.source === 'voice' ? voice : null;
    const attachments = uploadedFiles;
    const content = voiceInput?.text || textMessage || startRecording;
    const responseLanguage = voiceInput?.language || currentLanguage;
    if (!content.trim() && attachments.length === 0) return;
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(false);
    setDisplayAnimation(true);

    const formData = new FormData();
    formData.append('textMessage', content);
    formData.append('senderId', senderId);
    formData.append('sender', 'customer');
    formData.append('inputType', voiceInput ? 'voice' : inputType);
    formData.append('appId', appId);
    formData.append('languageCode', responseLanguage);

    attachments.forEach((fileObj) => {
      formData.append('images', fileObj.file);
    });

    const customerMessage = {
      senderId,
      sender: 'customer',
      text: content,
      timestamp: new Date().toISOString(),
      images: attachments.map((f) => URL.createObjectURL(f.file)),
    };

    followLatest();
    dispatch(addMessage(customerMessage));

    setTextMessage('');
    setStartRecording('');
    attachments.forEach((file) => URL.revokeObjectURL(file.preview));
    setUploadedFiles([]);
    form.resetFields();

    try {
      if (isHumanAgentActive) {
        await axios.put(`${rootAPI}/api/conversations/v1/send/response`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setDisplayAnimation(false);
      } else {
        const response = await axios.post(`${rootAPI}/api/basic/v2.0.0/conversations/message/send/${appId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Basic ${basicAuth}`,
          },
        });


        if (response.data.response) {
          setDisplayAnimation(false);
        }
      }

      refetch();
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Something went wrong.';
      message.error(errorMessage);
      setError(true);
      setDisplayAnimation(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  // HTTP REQUEST END

  useEffect(() => {
    if (startRecording && startRecording.trim()) {
      handleConversation();
    }
  }, [startRecording]);

  // Handle AI audio playback
  useEffect(() => {
    if (stateMessages.length > 0) {
      const lastMessage = stateMessages[stateMessages.length - 1];

      if (lastMessage.audioPath) {
        const audioUrl = `${API.audio}${lastMessage.audioPath}`;

        // console.log('🔊 Playing Audio:', audioUrl);

        audioRef.current.src = audioUrl;

        const playAudio = async () => {
          try {
            await audioRef.current.play();
          } catch (error) {
            setPlaybackBlocked(true);
            message.info('Tap the audio player to hear the response.');
          }
        };

        playAudio();
      }
    }
  }, [stateMessages]);

  // AUDIO CONTROLLER
  const stopAudio = () => {
    setRecordingStopSignal((value) => value + 1);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setRecording(false);
    }
  };

  const handleCloseWidget = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setRecording(false);
    setStartRecording('');
    window.parent.postMessage('close-widget', '*');
  };


  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        maxHeight: '-webkit-fill-available',
        overflow: 'hidden',
      }}
    >
      <Heading
        style={{
          flexShrink: 0,
          minHeight: '80px',
          padding: '10px 15px',
          // backgroundColor:'#eff6fc'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', paddingTop: '5px', paddingBottom: '5px' }}>
          <div
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: '#f0f0f0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {isImageLoading ? (
              <Spin size="small" />
            ) : (
              <img
                src={finalImageSrc}
                alt="AI icon"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
          </div>

          <div>
            <StyledNamearagraph>{app?.name}</StyledNamearagraph>
            <StyledStatusParagraph>
              <span>Status </span>-
              <span>
                {app?.app_id ? `${isHumanAgentActive ? 'Human Agent Connected' : 'Ai Connected'}` : 'Disconnected'}
              </span>
            </StyledStatusParagraph>
          </div>
        </div>
        <div>
          <div
            style={{
              cursor: 'pointer',
              padding: '6px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '50%',
              backgroundColor: '#f0f0f0',
              transition: 'background-color 0.2s ease',
              width: '30px',
              height: '30px',
              marginLeft: 'auto',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#bae7ff')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
            onClick={handleCloseWidget}
          >
            <CloseOutlined style={{ fontSize: '16px', color: '#555', fontWeight: 'bold' }} />
          </div>
          {/* <div style={{ display: 'flex', alignItems: 'center' }}> */}
          {/* <SwitcherContainer>
              <Switcher onClick={toggleSwitch} checked={isChecked}>
                <Toggle checked={isChecked} />
                <Label checked={isChecked}>{isChecked ? 'Bn' : 'En'}</Label>
              </Switcher>
            </SwitcherContainer> */}
          {/* <img
              style={{ height: '25px', width: '25px' }}
              src={require('../../static/img/banner/menu.png')}
              alt="Menu"
            /> */}
          {/* </div> */}
        </div>
      </Heading>

      <div
        ref={scrollContainerRef}
        style={{
          position: 'relative',
          flex: '1 1 0',
          minHeight: 0,
          overflowY: 'auto',
          overflowAnchor: 'none',
          padding: '10px',
        }}
      >
        <div ref={messageContentRef}>
        {/* Chat messages */}
        {stateMessages.map((chat, index) => (
          <ChatMessage key={index} chat={chat} senderId={senderId} appId={appId} />
        ))}
        {transcribing && <WaitingAnimation customer />}
        <div ref={messagesEndRef} />
        </div>

        {displayAnimation && <WaitingAnimation />}
        <VoiceOverlay recording={recording} isPlaying={isPlaying} stopAudio={stopAudio} />
      </div>

      <Form form={form} onFinish={handleConversation} style={{ flexShrink: 0 }}>
        <div style={{ padding: uploadedFiles.length ? '10px 10px 0' : 0 }}>
            {/* File preview section */}
            {uploadedFiles.length > 0 && (
              <div style={{ display: 'flex', gap: '5px', marginBottom: '5px', flexWrap: 'wrap' }}>
                {uploadedFiles.map((file, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img
                      src={file.preview}
                      alt="preview"
                      style={{ width: 100, height: 120, borderRadius: 4, objectFit: 'cover' }}
                    />
                    <CloseOutlined
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        background: '#fff',
                        borderRadius: '50%',
                        cursor: 'pointer',
                      }}
                      onClick={() => removeFile(index)}
                    />
                  </div>
                ))}
              </div>
            )}

        </div>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', padding: '10px' }}>
          <div style={{ flexGrow: 1 }}>
            <Form.Item style={{ margin: 0 }} name="query">
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <StyledInput
                  style={{ paddingRight: '80px' }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      handleConversation();
                    }
                  }}
                  placeholder="Ask me anything, I'm here to help!"
                  onChange={(e) => {
                    setTextMessage(e.target.value);
                    setInputType('text');
                  }}
                  value={textMessage}
                />
                <div
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    gap: '4px',
                    display: 'flex',
                  }}
                >
                  {/* Hidden File Input Trigger */}
                  <div
                    style={{
                      cursor: user?.permissions?.image ? 'pointer' : 'not-allowed',
                      opacity: user?.permissions?.image ? 1 : 0.5,
                    }}
                    onClick={() => {
                      if (!user?.permissions?.image) {
                        message.warning('Please upgrade your package to enable image uploads');
                        return;
                      }
                      fileInputRef.current.click();
                    }}
                  >
                    <CameraIcon src={require('../../static/img/chat/camera.png')} alt="Camera" />
                  </div>
                  {/* Send Button */}
                  <CameraIcon
                    onClick={handleConversation}
                    style={{ cursor: 'pointer' }}
                    src={require('../../static/img/chat/newSend.png')}
                    alt="Send"
                  />
                </div>
              </div>
            </Form.Item>
          </div>

          <RecordingIndicator recording={recording} />

          {!isPlaying ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  cursor: user?.permissions?.voice ? 'pointer' : 'not-allowed',
                  opacity: user?.permissions?.voice ? 1 : 0.5,
                  position: 'relative',
                }}
                onClick={() => {
                  if (!user?.permissions?.voice) {
                    message.warning('Please upgrade your package to enable voice messages');
                  }
                }}
              >
                <VoiceMessage
                  disabled={!user?.permissions?.voice || isSubmitting}
                  setRecording={setRecording}
                  setTranscribing={setTranscribing}
                  stopSignal={recordingStopSignal}
                  onTranscript={(text, detectedLanguage) => {
                    const spokenLanguage = detectedLanguage || (/[ঀ-৿]/u.test(text) ? 'bn-BD' : 'en-US');
                    setCurrentLanguage(spokenLanguage);
                    setIsChecked(spokenLanguage.startsWith('bn'));
                    return handleConversation({ source: 'voice', text, language: spokenLanguage });
                  }}
                  language={currentLanguage}
                />
              </div>
            </div>
          ) : (
            <MicrophoneIcon onClick={stopAudio} src={require('../../static/img/icon/mute.png')} alt="Microphone" />
          )}
        </div>

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          multiple
          onChange={handleFileChange}
        />
      </Form>
      <audio ref={audioRef} preload="auto" controls={playbackBlocked} style={playbackBlocked ? { width: '100%' } : undefined}></audio>
    </div>
  );
};

export default Conversation;
